/*eslint no-process-env: 0*/
// SSO helpers ported from oclmap/oclweb3 (common/utils.js): the OCL API
// brokers the Keycloak OIDC flow — /users/login/ redirects to
// sso.openconceptlab.org and back to LOGIN_REDIRECT_URL (/oidc/login here)
// with a ?code= that /users/oidc/code-exchange/ turns into a token.
import { getAPIURL } from './utils.js';
import GAService from '../services/GAService';
import { ensureIdToken, prepareSignOut, renewSessionSilently, settleForcedSignOut } from './ssoSession.js';
import { isTokenExpired } from './tokenExpiry.js';
import { rememberCheckoutIntent } from './checkout.js';

// DefinePlugin only substitutes literal process.env.X expressions, so each
// runtime var needs its own accessor (window global wins in production).
const RUNTIME = {
  LOGIN_REDIRECT_URL: () => window.LOGIN_REDIRECT_URL || process.env.LOGIN_REDIRECT_URL,
  OIDC_RP_CLIENT_ID: () => window.OIDC_RP_CLIENT_ID || process.env.OIDC_RP_CLIENT_ID,
};
const runtime = (name) => RUNTIME[name]();

const cleanRedirectURL = () => (runtime('LOGIN_REDIRECT_URL') || '').replace(/([^:]\/)\/+/g, '$1');

// PKCE (RFC 7636) + per-request state/nonce: the Keycloak client is public
// (no client secret shipped in the browser bundle), so the code_verifier /
// code_challenge pair proves this browser tab initiated the flow, and
// state/nonce are freshly generated per request instead of hardcoded
// literals — restoring real CSRF protection.
const PKCE_CODE_VERIFIER_KEY = 'pkce_code_verifier';
const OAUTH_STATE_KEY = 'oauth_state';

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++)
    str += String.fromCharCode(bytes[i]);
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateSecureRandomString = (length = 64) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
};

const generateCodeChallenge = async (codeVerifier) => {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

// Generates a fresh PKCE code_verifier for this login attempt, stashes it in
// sessionStorage (tab-scoped, cleared on tab close) so OIDLoginCallback can
// read it back for the token exchange, and returns the derived S256
// code_challenge to send to Keycloak.
const preparePKCECodeChallenge = async () => {
  const codeVerifier = generateSecureRandomString(64);
  sessionStorage.setItem(PKCE_CODE_VERIFIER_KEY, codeVerifier);
  return generateCodeChallenge(codeVerifier);
};

// Marks the state getRegisterURL sends. Keycloak echoes it back after the
// email-verification link, usually in a new tab, so OIDLoginCallback can tell
// a finished sign-up apart from other callbacks.
const SIGNUP_STATE_PREFIX = 'signup.';

// state is only meaningful for flows that echo it back (login/register);
// reset-password does not.
const prepareOAuthState = (prefix = '') => {
  const state = prefix + generateSecureRandomString(32);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  return state;
};

export const isSignupOAuthState = (state) => Boolean(state && state.startsWith(SIGNUP_STATE_PREFIX));

export const consumeStoredPKCECodeVerifier = () => {
  const codeVerifier = sessionStorage.getItem(PKCE_CODE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_CODE_VERIFIER_KEY);
  return codeVerifier;
};

// Returns true when there's nothing to validate (flows like reset-password
// never sent a state).
export const consumeAndValidateOAuthState = (returnedState) => {
  const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  return !returnedState || returnedState === storedState;
};

export const getLoginURL = async () => {
  const codeChallenge = await preparePKCECodeChallenge();
  const state = prepareOAuthState();
  const nonce = generateSecureRandomString(32);
  GAService.clearSignupFlow();
  return `${getAPIURL()}/users/login/?client_id=${runtime('OIDC_RP_CLIENT_ID')}&state=${state}&nonce=${nonce}&redirect_uri=${cleanRedirectURL()}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
};

export const getRegisterURL = async () => {
  const codeChallenge = await preparePKCECodeChallenge();
  const state = prepareOAuthState(SIGNUP_STATE_PREFIX);
  const nonce = generateSecureRandomString(32);
  GAService.recordSignupStart();
  return `${getAPIURL()}/users/signup/?client_id=${runtime('OIDC_RP_CLIENT_ID')}&state=${state}&nonce=${nonce}&redirect_uri=${cleanRedirectURL()}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
};

export const getResetPasswordURL = async () => {
  const codeChallenge = await preparePKCECodeChallenge();
  return `${getAPIURL()}/users/password/reset/?client_id=${runtime('OIDC_RP_CLIENT_ID')}&redirect_uri=${cleanRedirectURL()}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
};

export const getSSOLogoutURL = () => {
  const idToken = localStorage.getItem('id_token');
  return idToken
    ? `${getAPIURL()}/users/logout/?id_token_hint=${idToken}&post_logout_redirect_uri=${cleanRedirectURL()}`
    : null;
};

export const oidcCodeExchangePayload = (code, codeVerifier) => ({
  code,
  redirect_uri: cleanRedirectURL(),
  client_id: runtime('OIDC_RP_CLIENT_ID'),
  code_verifier: codeVerifier,
});

export const currentUserToken = () => localStorage.getItem('token');

export const SESSION_CHANGE_EVENT = 'ocl-session-change';
const announceSessionChange = () => window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));

export const onSessionChange = (listener) => {
  window.addEventListener(SESSION_CHANGE_EVENT, listener);
  const onStorage = (event) => { if (event.key === null || event.key === 'token') listener(); };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(SESSION_CHANGE_EVENT, listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const isLoggedIn = () => {
  const token = currentUserToken();
  return Boolean(token) && !isTokenExpired(token);
};

export const getCurrentUser = () => {
  if (!isLoggedIn())
    return null;
  const data = localStorage.getItem('user');
  return data ? JSON.parse(data) : null;
};

export const storeSession = (token, idToken) => {
  localStorage.setItem('token', token);
  if (idToken)
    localStorage.setItem('id_token', idToken);
  announceSessionChange();
};

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('user');
  announceSessionChange();
};

// Where to land after the OIDC round-trip (sign-in or sign-out) — set
// before leaving the site, consumed once by the /oidc/login callback.
export const rememberReturnTo = () =>
  sessionStorage.setItem('returnTo', window.location.pathname + window.location.search + window.location.hash);

export const consumeReturnTo = () => {
  const returnTo = sessionStorage.getItem('returnTo');
  sessionStorage.removeItem('returnTo');
  return returnTo && returnTo.startsWith('/') ? returnTo : '/';
};

// Click handler for every "sign up" button: come back to this page after the
// SSO round trip, then leave for the API's sign-up page.
export const startSignup = (event) => {
  if (event)
    event.preventDefault();
  rememberReturnTo();
  getRegisterURL().then(url => { window.location.href = url; });
};

// Click handler for every "Subscribe to Pro" button (OpenConceptLab/ocl_online#325): mark the
// trip and land on /pricing, signing in first if need be. /pricing then goes on to checkout
// (pages/pricing/useProCheckout.js), so every Pro button takes the same path.
export const startProCheckout = (event) => {
  if (event)
    event.preventDefault();
  rememberCheckoutIntent();
  if (isLoggedIn()) {
    window.location.assign('/pricing');
    return;
  }
  sessionStorage.setItem('returnTo', '/pricing');
  getLoginURL().then(url => { window.location.href = url; });
};

const exchangeSilentCode = body =>
  import('../services/APIService').then(module => module.default.post('/users/oidc/code-exchange/', body)).then(response => response.data);

// Sign out locally, then end the Keycloak session (which redirects back).
export const logout = () => ensureIdToken(exchangeSilentCode).then(() => {
  const ssoLogout = getSSOLogoutURL();
  rememberReturnTo();
  clearSession();
  if (ssoLogout)
    window.location.href = ssoLogout;
  else
    window.location.href = '/';
});

// As oclweb3's forced sign-out, minus the sign-in prompt afterwards: most of this site is public.
const forceSignOut = () => {
  const { logoutURL } = prepareSignOut({
    forced: true,
    returnTo: window.location.origin + window.location.pathname + window.location.search,
    apiURL: getAPIURL(),
    idToken: localStorage.getItem('id_token'),
    loginRedirectURL: cleanRedirectURL(),
  });
  rememberReturnTo();
  clearSession();
  if (logoutURL)
    window.location.href = logoutURL;
};

let signingOut = null;
const logoutUser = () => {
  signingOut = signingOut || ensureIdToken(exchangeSilentCode).then(forceSignOut).catch(error => {
    signingOut = null;
    throw error;
  });
  return signingOut;
};

export const refreshCurrentUser = () =>
  import('../services/APIService').then(module => module.default.get('/user/', { includeSubscribedOrgs: true, includeFollowing: true })).then(profile => {
    if (profile.status === 200)
      localStorage.setItem('user', JSON.stringify(profile.data));
  });

// Renew silently while the Keycloak session lives on, else sign out; the reload re-sends requests with the new token.
let renewing = null;
export const renewOrSignOut = () => {
  renewing = renewing || renewSessionSilently(body => exchangeSilentCode(body)).then(tokens => {
    if (!tokens || !tokens.id_token)
      return logoutUser();
    storeSession(tokens.access_token, tokens.id_token);
    settleForcedSignOut();
    const reload = () => window.location.reload();
    return refreshCurrentUser().then(reload, reload);
  });
  return renewing;
};
